import type { ApiWindow, StatusAreaSummary } from "./apiTypes";
import { computeGlance, GLANCE_ORDER } from "./glance";
import type { Glance, GlanceTier } from "./glance";
import { formatManilaClockRange, formatWindowDuration, manilaDayLabel } from "./countdown";

export interface UpNextArea {
  key: string;
  name: string;
  mapped: boolean;
  glance: Glance;
  provisional?: boolean;
}

export interface UpNextSlot {
  start: string;
  end: string;
  localLabel?: string;
  phase: "now" | "future";
  tier: GlanceTier;
  areas: UpNextArea[];
}

interface SlotGroup {
  window: ApiWindow;
  entries: UpNextArea[];
}

function compareUpNextAreas(a: UpNextArea, b: UpNextArea): number {
  if (a.mapped !== b.mapped) {
    return a.mapped ? -1 : 1;
  }
  return a.name.localeCompare(b.name);
}

/**
 * Groups every area's driving outage window (the same window `computeGlance` uses to decide its tier)
 * into chronological slots. Areas with no pending window (tier `clear`/`unknown`) are skipped.
 */
export function buildUpNext(
  areas: readonly StatusAreaSummary[],
  names: ReadonlyMap<string, string>,
  now: Date,
): UpNextSlot[] {
  const groups = new Map<string, SlotGroup>();

  for (const area of areas) {
    const glance = computeGlance(area, now);
    const window = glance.window;
    if (!window) {
      continue;
    }

    const upNextArea: UpNextArea = {
      key: area.key,
      name: names.get(area.key) ?? area.key,
      mapped: area.mapped,
      glance,
      provisional: area.provisional,
    };

    const groupKey = `${window.start}|${window.end}`;
    const group = groups.get(groupKey);
    if (group) {
      group.entries.push(upNextArea);
    } else {
      groups.set(groupKey, { window, entries: [upNextArea] });
    }
  }

  const slots: UpNextSlot[] = [];
  for (const group of groups.values()) {
    let tier: GlanceTier = group.entries[0]?.glance.tier ?? "clear";
    let bestOrder = GLANCE_ORDER[tier];
    for (const entry of group.entries) {
      const order = GLANCE_ORDER[entry.glance.tier];
      if (order < bestOrder) {
        bestOrder = order;
        tier = entry.glance.tier;
      }
    }

    slots.push({
      start: group.window.start,
      end: group.window.end,
      localLabel: group.window.localLabel,
      phase: tier === "off" ? "now" : "future",
      tier,
      areas: [...group.entries].sort(compareUpNextAreas),
    });
  }

  slots.sort((a, b) => {
    if (a.start !== b.start) {
      return a.start < b.start ? -1 : 1;
    }
    if (a.end !== b.end) {
      return a.end < b.end ? -1 : 1;
    }
    return 0;
  });

  return slots;
}

export function formatSlotHeading(slot: UpNextSlot, now: Date): { clock: string; day: string } {
  const window: ApiWindow = { start: slot.start, end: slot.end, localLabel: slot.localLabel };
  return {
    clock: formatManilaClockRange(window),
    day: manilaDayLabel(new Date(slot.start), now),
  };
}

/** "5 hr 3 min" / "2 days 4 hr" (no minutes once the span reaches a day) / "3 min" — minutes rounded up, never "0 min". */
function formatDuration(ms: number): string {
  const totalMinutes = Math.max(1, Math.ceil(ms / 60000));
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;

  const parts: string[] = [];
  if (days > 0) {
    parts.push(`${days} ${days === 1 ? "day" : "days"}`);
  }
  if (hours > 0) {
    parts.push(`${hours} hr`);
  }
  if (days === 0 && minutes > 0) {
    parts.push(`${minutes} min`);
  }

  return parts.length > 0 ? parts.join(" ") : `${minutes} min`;
}

function formatRelText(now: Date, targetIso: string, kind: "starts_in" | "ends_in"): string {
  const diffMs = new Date(targetIso).getTime() - now.getTime();
  if (diffMs <= 0) {
    const word = kind === "starts_in" ? "Started" : "Ended";
    return `${word} ${formatDuration(-diffMs)} ago`;
  }
  const prefix = kind === "starts_in" ? "Starts in" : "Ends in";
  return `${prefix} ${formatDuration(diffMs)}`;
}

export function renderUpNext(container: HTMLElement, slots: readonly UpNextSlot[], now: Date): void {
  container.textContent = "";

  if (slots.length === 0) {
    const empty = document.createElement("p");
    empty.className = "muted upnext-empty";
    empty.textContent = "No outage windows announced right now.";
    container.appendChild(empty);
    return;
  }

  for (const slot of slots) {
    const details = document.createElement("details");
    details.className = "upnext-slot";
    details.dataset.tier = slot.tier;
    details.dataset.phase = slot.phase;
    details.open = slot.phase === "now" || slot.tier === "soon";

    const summary = document.createElement("summary");
    summary.className = "upnext-summary";

    const heading = formatSlotHeading(slot, now);
    const timeSpan = document.createElement("span");
    timeSpan.className = "upnext-time";
    const clockSpan = document.createElement("span");
    clockSpan.className = "upnext-clock";
    clockSpan.textContent = heading.clock;
    const daySpan = document.createElement("span");
    daySpan.className = "upnext-day";
    daySpan.textContent = heading.day;
    timeSpan.appendChild(clockSpan);
    timeSpan.appendChild(daySpan);
    const duration = formatWindowDuration({ start: slot.start, end: slot.end });
    if (duration) {
      const durationSpan = document.createElement("span");
      durationSpan.className = "upnext-duration";
      durationSpan.textContent = duration;
      timeSpan.appendChild(durationSpan);
    }

    const relKind: "starts_in" | "ends_in" = slot.phase === "future" ? "starts_in" : "ends_in";
    const relTarget = slot.phase === "future" ? slot.start : slot.end;
    const relSpan = document.createElement("span");
    relSpan.className = "rel-time";
    relSpan.dataset.relTarget = relTarget;
    relSpan.dataset.relKind = relKind;
    relSpan.textContent = formatRelText(now, relTarget, relKind);

    const countSpan = document.createElement("span");
    countSpan.className = "upnext-count";
    countSpan.textContent = `${slot.areas.length} ${slot.areas.length === 1 ? "area" : "areas"}`;

    summary.appendChild(timeSpan);
    summary.appendChild(relSpan);
    summary.appendChild(countSpan);

    const areasList = document.createElement("div");
    areasList.className = "upnext-areas";
    areasList.setAttribute("role", "list");

    for (const area of slot.areas) {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "upnext-area area-list-item";
      button.dataset.areaKey = area.key;
      button.dataset.tier = area.glance.tier;
      button.setAttribute("role", "listitem");

      const nameSpan = document.createElement("span");
      nameSpan.className = "area-name";
      nameSpan.textContent = area.name;

      const pill = document.createElement("span");
      pill.className = "tier-pill";
      pill.dataset.tier = area.glance.tier;
      pill.textContent = area.glance.label;

      button.appendChild(nameSpan);
      if (area.provisional) {
        const tag = document.createElement("span");
        tag.className = "estimated-tag";
        tag.textContent = "Estimated";
        button.appendChild(tag);
      }
      button.appendChild(pill);

      if (!area.mapped) {
        const unmapped = document.createElement("span");
        unmapped.className = "area-unmapped";
        unmapped.textContent = "No polygon yet";
        button.appendChild(unmapped);
      }

      areasList.appendChild(button);
    }

    details.appendChild(summary);
    details.appendChild(areasList);
    container.appendChild(details);
  }
}
